<template>
  <div
    v-if="(boughtProduct && totalPrice !== null) || cartStore.addedToCartCount > 0"
    class="
    fixed bottom-0 desk:bottom-auto desk:right-32 right-0 z-modalsContent
    bg-bg-base outline outline-bg-inverse/10 shadow-lg w-full desk:w-[480px]"
    :class="{
      'desk:top-[85px]': isScrolled && scrollPos > 44,
      'desk:top-[161px]': !isScrolled || scrollPos <= 44,
    }"
  >
    <div
      class="shrink-0 grow-0 basis-56
             flex w-full items-center justify-between
             px-16 border-b border-border-subtle
             select-none py-16"
    >
      <div class="type-headline-lg leading-single flex items-start">
        {{ $t('addedToCart.headline') }}
      </div>
      <button
        class="
          w-24 h-24 text-right cursor-pointer
          outline outline-4 outline-transparent rounded-6
          hover:outline-bg-secondary hover:bg-bg-secondary transition-all"
        type="button"
        @click="uiStore.setShowAddedToCart(false)"
      >
        <img
          src="/icons/close.svg"
          class="h-24 w-24 inline-block"
          alt="Close"
        >
      </button>
    </div>
    <div class="p-16">
      <!-- * * * * Multiple Items Added To Cart * * * * -->
      <div v-if="cartStore.addedToCartCount > 1">
        <span class="type-base">{{ $t('addedToCart.numProductsAdded', {num: cartStore.addedToCartCount}) }}</span>
      </div>
      <div v-else class="flex justify-between">
        <!-- Pointer-only duplicate of the name link below; hidden from AT -->
        <nuxt-link
          :to="productUrl(boughtProduct.url)"
          aria-hidden="true"
          tabindex="-1"
          class="w-80 aspect-product relative"
        >
          <div v-if="boughtProduct.image">
            <nuxt-img
              preset="standard"
              provider="norce"
              loading="lazy"
              :src="boughtProduct.image"
              class="w-full h-full object-cover"
              :alt="boughtProduct.name"
            />
            <div class="imageOverlay absolute top-0 left-0 w-full h-full" />
          </div>
          <img
            v-else
            src="/icons/no-image.svg"
            class="aspect-product w-64 rounded-6"
            alt=""
          >
          <div v-if="userPrice?.price?.priceBeforeVat === '0'" class="absolute -top-6 -left-6 bg-lightest h-28 w-28 rounded-full flex items-center justify-center">
            <div class="h-24 w-24 rounded-full bg-bg-accenture flex items-center justify-center">
              <img
                class="w-12 h-12"
                src="/icons/gift.svg"
                alt=""
              >
            </div>
          </div>
        </nuxt-link>
        <div
          class="w-full flex justify-between ml-12"
        >
          <div class="flex flex-col gap-2">
            <div class="type-headline-sm">
              <nuxt-link :to="productUrl(boughtProduct.url)">
                {{ boughtProduct.name }}
              </nuxt-link>
            </div>
            <div class="type-sm text-text-subtle">
              {{ boughtProduct.partNo }}
            </div>
            <div v-if="cartStore.currentlyBuyingQuantity > 0 && cartStore.currentlyBuyingQuantity !== totalInCart" class="type-sm text-text-subtle">
              {{ $t('order.quantityPrefix.added') }} {{ cartStore.currentlyBuyingQuantity }}
            </div>
          </div>
          <div
            v-if="userPrice.status !== 'pending'"
            class="flex flex-col justify-start items-end text-right gap-2"
          >
            <div class="type-sm text-text-subtle">
              {{ $t('order.quantityPrefix.total') }} <span>{{ totalInCart }}</span>
            </div>
            <div
              class="type-headline-sm whitespace-nowrap"
              :class="{'text-text-errorStrong': shouldShowDiscount}"
            >
              {{ productPrice(totalPrice, 'price', true) }}
            </div>

            <div
              v-if="shouldShowDiscount && priceByQuantity.priceBeforeDiscount > 0"
              class="type-sm text-text-subtle line-through whitespace-nowrap"
            >
              {{ productPrice(totalPrice, 'priceBeforeDiscount', true) }}
            </div>
          </div>
        </div>
      </div>
      <nuxt-link :to="$manualUrl('/checkout')" class="btn w-full mt-16 group">
        {{ $t('minicart.toCheckout') }}
      </nuxt-link>
      <button
        class="btn btn--secondary w-full mt-8"
        type="button"
        @click="uiStore.setShowAddedToCart(false)"
      >
        {{ $t('checkout.backToShopping') }}
      </button>
    </div>
  </div>
  <!--- <img alt="" src="/icons/arrow-right-inv.svg" class="inline-block w-16 h-16 mr-12"> ---->
</template>

<script setup lang="ts">
import { useUiStore } from '~/store/ui';
import { useCartStore } from '~/store/cart';
import useLinkReplacer from '~/composables/useLinkReplacer';
import type { ProductRaw, ProductVariantOld } from '#constants/types/algolia';
import { useUserStore } from '~/store/user';
import { useLocalizedCurrency } from '~/composables/useLocalizedCurrency';
import usePrices from '~/composables/usePrices';
import { useGlobalContentStore } from '~/store/globalContent';
const { productUrl } = useLinkReplacer();

const uiStore = useUiStore();
const cartStore = useCartStore();
const globalStore = useGlobalContentStore();
const { $toNumber } = useNuxtApp();
const { productPrice } = usePrices();

defineProps<{
  scrollPos: number;
  isScrolled: boolean;
}>();

const boughtProduct = ref<ProductRaw | ProductVariantOld | null>(null);
const userStore = useUserStore();

onMounted(async()=> {
  boughtProduct.value = cartStore.currentlyBuying;
  // Fallback to cart item when currentlyBuying is not set
  if (!boughtProduct.value && cartStore.justAddedPartNo) {
    const cartItem = cartStore.cart.items.find(item => item.partNo === cartStore.justAddedPartNo);
    if (cartItem) {
      boughtProduct.value = cartItem as any;
    }
  }
});

onBeforeUnmount(()=> {
  cartStore.setAddedToCartCount(0);
});

const userPrice = computed(() => {
  const partNo = cartStore.currentlyBuying?.partNo || cartStore.justAddedPartNo || '';
  return userStore.getPrice(partNo);
});

const totalPrice = computed(()=> {
  const partNo = cartStore.currentlyBuying?.partNo || cartStore.justAddedPartNo || '';
  const productTotal = cartStore.cart.items.find(f => f.partNo === partNo);
  if (productTotal) {
    return productTotal.priceInfoTotal;
  }
  return null;
});

const totalInCart = computed(()=> {
  const partNo = cartStore.currentlyBuying?.partNo || cartStore.justAddedPartNo || '';
  const productTotal = cartStore.cart.items.find(f => f.partNo === partNo);
  if (productTotal) {
    return productTotal.quantity;
  }
  return 0;
});

const priceByQuantity = computed(() => {
  return {
    price: $toNumber(userPrice.value?.price?.priceBeforeVat || '0') * cartStore.currentlyBuyingQuantity,
    priceBeforeDiscount: $toNumber(userPrice.value?.price?.priceBeforeDiscountBeforeVat || '0') * cartStore.currentlyBuyingQuantity,
  };
});

const shouldShowDiscount = computed(() => {
  if (!totalPrice.value || !userPrice.value?.price?.isOnSale) {
    return false;
  }
  if (globalStore.getIsB2c) {
    // On B2C, only show discount when item is on a campaign price
    return totalPrice.value.isCampaignPrice === true;
  }
  // Show discount when: NOT hiding SRP OR there's actual discount/campaign
  return !globalStore.hideSRP ||
         (totalPrice.value.discountPercentage ?? 0) > 0 ||
         totalPrice.value.isCampaignPrice;
});
</script>
